import * as React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import styles from './DocumentListItem.sass'
import Document from 'documents/Document'
import Avatar from 'components/Avatar'
import { Dropdown, DropdownItem, DropdownTitle } from 'components/Dropdown'
import Icon from 'components/Icon'
import RadialProgress from 'components/RadialProgress'
import { IconButton } from 'components/IconButton'
import JobDocumentsViewModel from '../../../jobs/views/JobRoot/Job/JobDocumentsViewModel'
import Spinner from 'components/Spinner'
import Pill from 'components/Pill'
import Checkbox from 'components/Checkbox'
import FileTypeIcon from 'components/FileTypeIcon'
import { IconType } from 'utils/icon-util'
import Member from 'iam/models/Member'

interface Props {
  document: Document
  documentsVM?: JobDocumentsViewModel
  documentClickAction(document: Document): void
  noRead?: boolean
  spacing?: boolean
  selectable?: boolean
  showIcon?: boolean
}

export default observer(function DocumentListItem({
  document,
  documentsVM,
  documentClickAction,
  noRead,
  spacing,
  selectable,
  showIcon = true,
}: Props) {
  const icon = IconType(document.title || '')
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
  })

  const { type } = document

  return (
    <div
      className={cx(styles.root, spacing && styles.spacing)}
      data-test={`document-list-item-${document.title}`}
    >
      {selectable && (
        <div className={styles.checkboxContainer}>
          <Checkbox
            checked={documentsVM?.isSelected(document)}
            disabled={document.archived || document.isPurged}
            onChange={() => documentsVM?.selectDocument(document)}
          />
        </div>
      )}
      <div
        onClick={() => documentClickAction(document)}
        className={styles.itemContainer}
      >
        {showIcon && (
          <div className={styles.icon}>
            <FileTypeIcon
              height={36}
              primaryColor={icon.color}
              strokeColor="#bcbcbe"
              type={icon.type}
            />
          </div>
        )}
        <div className={styles.info}>
          {document.archived && (
            <div className={styles.archived}>
              <Pill color="red" shape="square" trim>
                Archived
              </Pill>
            </div>
          )}
          {document.isPurged &&
            !document.uploadProgress &&
            !document.archived && (
              <div className={styles.metaItem}>
                <Pill color="red" shape="square" trim>
                  Purged
                </Pill>
              </div>
            )}
          {document.isManualAttestationRequired && !document.archived && (
            <div className={styles.archived}>
              <Pill color="red" shape="square" trim>
                Manual Verification Required
              </Pill>
            </div>
          )}
          <div className={styles.title}>
            <span className={styles.primaryTitle}>{document.title}</span>
          </div>
          <div className={styles.dateModified}>
            <p>{dateFormatter.format(document.dateModified)}</p>
          </div>
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <Avatar
                size={20}
                subject={
                  document.author !== Member.none
                    ? document.author
                    : document.owner
                }
              />
            </div>
            {type && (
              <div className={styles.metaItem}>
                <div className={styles.divider} />
              </div>
            )}
            {type && (
              <div className={styles.metaItem}>
                <Pill stout>{type.name}</Pill>
              </div>
            )}
          </div>
        </div>
        <ItemIcon
          document={document}
          documentsVM={documentsVM}
          noRead={noRead}
        />
      </div>
    </div>
  )
})

interface ItemIconProps {
  document: Document
  documentsVM?: JobDocumentsViewModel
  noRead?: boolean
}

const ItemIcon = observer(function ItemIcon({
  document,
  documentsVM,
  noRead,
}: ItemIconProps) {
  if (document.uploadProgress) {
    return (
      <span className={styles.status}>
        <RadialProgress size={24} progress={document.uploadProgress.percent} />
      </span>
    )
  }
  switch (document.stage.type) {
    case 'Preprocessing':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing a virus scan"
        >
          <Spinner />
        </span>
      )
    case 'AttestationScreening':
      return (
        <span
          className={styles.status}
          title="This document is currently undergoing client data verification"
        >
          <Spinner />
        </span>
      )
    case 'ManualAttestationRequired':
      return (
        <span
          className={cx(styles.status, styles.manualVerificationRequired)}
          title="This document requires manual verification before it becomes available"
        >
          <Icon name="alert" />
        </span>
      )
    case 'Rejected':
      return document.stage.rejection === 'Quarantined' ? (
        <span
          className={cx(styles.status, styles.virus)}
          title="This document is infected with a virus and is not available for download"
        >
          <Icon name="virus" />
        </span>
      ) : (
        <span
          className={cx(styles.status, styles.failed)}
          title="This document has failed to upload"
        >
          <Icon name="info" />
        </span>
      )
  }

  if (document.downloadable && !noRead && documentsVM) {
    return (
      <span className={styles.status}>
        <Dropdown
          width="medium"
          autoDismiss
          position="bottom-end"
          button={
            <span className={styles.hover}>
              <IconButton
                data-test={`download-actions-dropdown-${document.title}`}
              >
                <Icon name="more" />
              </IconButton>
            </span>
          }
        >
          <DropdownTitle>More</DropdownTitle>
          <ViewInlineAction document={document} documentsVM={documentsVM} />
          <DownloadAction document={document} documentsVM={documentsVM} />
          {document.canManageLinks && (
            <UnlinkInlineAction document={document} documentsVM={documentsVM} />
          )}
          {documentsVM.canRemoveDocs && (
            <DeleteInlineAction document={document} documentsVM={documentsVM} />
          )}
        </Dropdown>
      </span>
    )
  }
  return null
})

const DownloadAction = observer(function DownloadAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  if (!document.downloadable) {
    return null
  }

  return (
    <div>
      <DropdownItem
        icon={<Icon name="download" />}
        muteClickEvent
        onClick={() => {
          documentsVM.download(document, false)
        }}
      >
        Download
      </DropdownItem>
    </div>
  )
})

const ViewInlineAction = observer(function ViewInlineAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  if (!document.downloadable) {
    return null
  }

  return (
    <div>
      <DropdownItem
        icon={<Icon name="view" />}
        muteClickEvent
        onClick={() => {
          documentsVM.download(document, true)
        }}
      >
        View
      </DropdownItem>
    </div>
  )
})

const UnlinkInlineAction = observer(function UnlinkInlineAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  return (
    <DropdownItem
      icon={<Icon name="link" />}
      muteClickEvent
      onClick={() => {
        documentsVM.unlinkDocument(document)
      }}
    >
      Unlink
    </DropdownItem>
  )
})

const DeleteInlineAction = observer(function DeleteInlineAction({
  document,
  documentsVM,
}: {
  document: Document
  documentsVM: JobDocumentsViewModel
}) {
  return (
    <div>
      <DropdownItem
        icon={<Icon name="delete" />}
        muteClickEvent
        onClick={() => {
          documentsVM.delete(document)
        }}
      >
        Delete
      </DropdownItem>
    </div>
  )
})
